// ======================================================================
// FILE:
// src/modules/finance/transactions/contra_voucher/pages/ContraVoucherCreatePage.js
//
// MODULE:
// Finance & Accounts Management System (FBAMS)
//
// FEATURE:
// Contra Voucher - Create
//
// PURPOSE:
// Create Contra Voucher Draft.
//
// ACCOUNTING:
// From Account -> Credit
// To Account   -> Debit
//
// IMPORTANT:
// - Uses existing Finance shared components
// - Uses existing financeLookupService
// - Uses contraVoucherService
// - No Redux
// - No accounting logic
// - Service owns business validation
// - Route/API owns transaction commit
// ======================================================================

import React, {
    useMemo,
    useState,
} from "react";

import {
    useNavigate,
} from "react-router-dom";

import AsyncSelect
    from "react-select/async";

import FinancePageHeader
    from "../../../components/FinancePageHeader";

import contraVoucherService
    from "../services/contraVoucherService";

import financeLookupService
    from "../../../services/financeLookupService";


// ======================================================================
// INITIAL FORM
// ======================================================================

const getInitialForm = () => ({

    company_id: null,
    company_option: null,

    voucher_date:
        new Date()
            .toISOString()
            .slice(0, 10),

    from_account_id: null,
    from_account_option: null,

    to_account_id: null,
    to_account_option: null,

    currency_id: null,
    currency_option: null,

    exchange_rate: "1",

    reference_no: "",

    reference_date: "",

    remarks: "",

});


// ======================================================================
// INITIAL LINE
// ======================================================================

const getInitialLine = (
    sequenceNo = 1
) => ({

    sequence_no: sequenceNo,

    transaction_amount: "",

    base_amount: "",

    cost_center_id: null,
    cost_center_option: null,

    

    description: "",

});


// ======================================================================
// COMPONENT
// ======================================================================

export default function ContraVoucherCreatePage() {

    const navigate =
        useNavigate();


    // ==================================================================
    // STATE
    // ==================================================================

    const [
        formData,
        setFormData
    ] = useState(
        getInitialForm()
    );


    const [
        lines,
        setLines
    ] = useState([
        getInitialLine(1)
    ]);


    const [
        saving,
        setSaving
    ] = useState(false);


    const [
        errorMessage,
        setErrorMessage
    ] = useState("");


    // ==================================================================
    // COMPANY LOOKUP
    // ==================================================================

    async function loadCompanies(
        inputValue = ""
    ) {

        try {

            const response =
                await financeLookupService
                    .searchCompanies(
                        inputValue
                    );

            return (
                response || []
            ).map(
                company => ({

                    value:
                        company.id,

                    label:
                        company.company_name,

                    data:
                        company,

                })
            );

        }
        catch (error) {

            console.error(
                "Company lookup failed:",
                error
            );

            return [];

        }

    }


    // ==================================================================
    // CHART OF ACCOUNT LOOKUP
    // ==================================================================

    async function loadAccounts(
        inputValue = ""
    ) {

        if (
            !formData.company_id
        ) {

            return [];

        }


        try {

            const response =
                await financeLookupService
                    .searchChartOfAccounts(
                        formData.company_id,
                        inputValue,
                        null,
                        20
                    );

            return (
                response || []
            ).map(
                account => ({

                    ...account,

                    value:
                        account.id,

                    label:
                        account.account_name
                            ? (
                                `${account.account_code || ""}`
                                + (
                                    account.account_code
                                        ? " - "
                                        : ""
                                )
                                + account.account_name
                            )
                            : (
                                account.name
                                || `Account ${account.id}`
                            ),

                })
            );

        }
        catch (error) {

            console.error(
                "Chart of Account lookup failed:",
                error
            );

            return [];

        }

    }


    // ==================================================================
    // CURRENCY LOOKUP
    // ==================================================================

    async function loadCurrencies(
        inputValue = ""
    ) {

        try {

            const response =
                await financeLookupService
                    .searchCurrencies(
                        inputValue
                    );

            return (
                response || []
            ).map(
                currency => ({

                    value:
                        currency.id ||
                        currency.value,

                    label:
                        currency.currency_name ||
                        currency.currency_code ||
                        currency.label,

                    data:
                        currency,

                })
            );

        }
        catch (error) {

            console.error(
                "Currency lookup failed:",
                error
            );

            return [];

        }

    }


    // ==================================================================
    // COST CENTRE LOOKUP
    // ==================================================================

    async function loadCostCenters(
        inputValue = ""
    ) {

        if (
            !formData.company_id
        ) {

            return [];

        }


        try {

            const response =
                await financeLookupService
                    .searchCostCenters(
                        formData.company_id,
                        inputValue,
                        20
                    );

            return (
                response || []
            ).map(
                costCenter => ({

                    ...costCenter,

                    value:
                        costCenter.id,

                    label:
                        costCenter.name ||
                        costCenter.cost_center_name ||
                        costCenter.label ||
                        `Cost Centre ${costCenter.id}`,

                })
            );

        }
        catch (error) {

            console.error(
                "Cost Centre lookup failed:",
                error
            );

            return [];

        }

    }


    // ==================================================================
    // PROJECT LOOKUP
    // ==================================================================

    

    // ==================================================================
    // HEADER CHANGE
    // ==================================================================

    function handleFieldChange(
        field,
        value,
        option = null
    ) {

        setFormData(
            previous => {

                const updated = {

                    ...previous,

                    [field]:
                        value,

                };


                // ------------------------------------------------------
                // COMPANY
                // ------------------------------------------------------

                if (
                    field ===
                    "company_id"
                ) {

                    updated.company_option =
                        option;

                    /*
                     * Accounts and dimensions belong
                     * to the selected company.
                     */

                    updated.from_account_id =
                        null;

                    updated.from_account_option =
                        null;

                    updated.to_account_id =
                        null;

                    updated.to_account_option =
                        null;

                    setLines(
                        previousLines =>
                            previousLines.map(
                                line => ({

                                    ...line,

                                    cost_center_id:
                                        null,

                                    cost_center_option:
                                        null,

                                    
                                })
                            )
                    );

                }


                // ------------------------------------------------------
                // FROM ACCOUNT
                // ------------------------------------------------------

                if (
                    field ===
                    "from_account_id"
                ) {

                    updated.from_account_option =
                        option;

                }


                // ------------------------------------------------------
                // TO ACCOUNT
                // ------------------------------------------------------

                if (
                    field ===
                    "to_account_id"
                ) {

                    updated.to_account_option =
                        option;

                }


                // ------------------------------------------------------
                // CURRENCY
                // ------------------------------------------------------

                if (
                    field ===
                    "currency_id"
                ) {

                    updated.currency_option =
                        option;

                }


                return updated;

            }
        );


        setErrorMessage("");

    }


    // ==================================================================
    // LINE CHANGE
    // ==================================================================

    function handleLineChange(
        index,
        field,
        value,
        option = null
    ) {

        setLines(
            previous => {

                const updated =
                    [...previous];


                updated[index] = {

                    ...updated[index],

                    [field]:
                        value,

                };


                if (
                    field ===
                    "cost_center_id"
                ) {

                    updated[index]
                        .cost_center_option =
                            option;

                }


                

                return updated;

            }
        );


        setErrorMessage("");

    }


    // ==================================================================
    // ADD LINE
    // ==================================================================

    function addLine() {

        setLines(
            previous => [

                ...previous,

                getInitialLine(
                    previous.length + 1
                ),

            ]
        );

    }


    // ==================================================================
    // REMOVE LINE
    // ==================================================================

    function removeLine(
        index
    ) {

        if (
            lines.length <= 1
        ) {

            return;

        }


        setLines(
            previous => {

                const updated =
                    previous.filter(
                        (
                            _line,
                            lineIndex
                        ) =>
                            lineIndex !==
                            index
                    );


                return updated.map(
                    (
                        line,
                        lineIndex
                    ) => ({

                        ...line,

                        sequence_no:
                            lineIndex + 1,

                    })
                );

            }
        );

    }


    // ==================================================================
    // TOTALS
    // ==================================================================

    const totalAmount =
        useMemo(
            () =>
                lines.reduce(
                    (
                        total,
                        line
                    ) =>
                        total +
                        (
                            Number(
                                line.transaction_amount
                            ) || 0
                        ),
                    0
                ),
            [
                lines
            ]
        );


    const totalBaseAmount =
        useMemo(
            () =>
                lines.reduce(
                    (
                        total,
                        line
                    ) => {

                        const amount =
                            Number(
                                line.transaction_amount
                            ) || 0;

                        const baseAmount =
                            Number(
                                line.base_amount
                            );

                        return (
                            total +
                            (
                                baseAmount > 0
                                    ? baseAmount
                                    : (
                                        amount *
                                        (
                                            Number(
                                                formData.exchange_rate
                                            ) || 1
                                        )
                                    )
                            )
                        );

                    },
                    0
                ),
            [
                lines,
                formData.exchange_rate,
            ]
        );


    // ==================================================================
    // VALIDATION
    // ==================================================================

    function validateForm() {

        const errors = [];


        if (
            !formData.company_id
        ) {

            errors.push(
                "Company is required."
            );

        }


        if (
            !formData.voucher_date
        ) {

            errors.push(
                "Voucher Date is required."
            );

        }


        if (
            !formData.from_account_id
        ) {

            errors.push(
                "From Account is required."
            );

        }


        if (
            !formData.to_account_id
        ) {

            errors.push(
                "To Account is required."
            );

        }


        if (
            formData.from_account_id &&
            formData.to_account_id &&
            Number(
                formData.from_account_id
            ) === Number(
                formData.to_account_id
            )
        ) {

            errors.push(
                "From Account and To Account cannot be the same."
            );

        }


        if (
            !formData.currency_id
        ) {

            errors.push(
                "Currency is required."
            );

        }


        if (
            Number(
                formData.exchange_rate
            ) <= 0
        ) {

            errors.push(
                "Exchange Rate must be greater than zero."
            );

        }


        if (
            !lines.length
        ) {

            errors.push(
                "At least one allocation line is required."
            );

        }


        lines.forEach(
            (
                line,
                index
            ) => {

                if (
                    Number(
                        line.transaction_amount
                    ) <= 0
                ) {

                    errors.push(
                        `Amount is required for line ${index + 1}.`
                    );

                }

            }
        );


        if (
            totalAmount <= 0
        ) {

            errors.push(
                "Contra Voucher amount must be greater than zero."
            );

        }


        return errors;

    }


    // ==================================================================
    // BUILD PAYLOAD
    // ==================================================================

    function buildPayload() {

        const exchangeRate =
            Number(
                formData.exchange_rate
            ) || 1;


        return {

            company_id:
                Number(
                    formData.company_id
                ),

            voucher_date:
                formData.voucher_date,

            from_account_id:
                Number(
                    formData.from_account_id
                ),

            to_account_id:
                Number(
                    formData.to_account_id
                ),

            currency_id:
                Number(
                    formData.currency_id
                ),

            exchange_rate:
                exchangeRate,

            reference_no:
                formData.reference_no
                    ?.trim() ||
                null,

            reference_date:
                formData.reference_date ||
                null,

            remarks:
                formData.remarks
                    ?.trim() ||
                null,

            lines:
                lines.map(
                    (
                        line,
                        index
                    ) => {

                        const amount =
                            Number(
                                line.transaction_amount
                            ) || 0;


                        return {

                            sequence_no:
                                index + 1,

                            transaction_amount:
                                amount,

                            base_amount:
                                (
                                    Number(
                                        line.base_amount
                                    ) ||
                                    (
                                        amount *
                                        exchangeRate
                                    )
                                ),

                            cost_center_id:
                                line.cost_center_id
                                    ? Number(
                                        line.cost_center_id
                                    )
                                    : null,

                            

                            description:
                                line.description
                                    ?.trim() ||
                                null,

                            source_type:
                                null,

                            source_id:
                                null,

                            source_reference:
                                null,

                        };

                    }
                ),

        };

    }


    // ==================================================================
    // SAVE DRAFT
    // ==================================================================

    async function handleSaveDraft() {

        const errors =
            validateForm();


        if (
            errors.length
        ) {

            setErrorMessage(
                errors.join(" ")
            );

            return;

        }


        try {

            setSaving(
                true
            );

            setErrorMessage("");


            const payload =
                buildPayload();


            const response =
                await contraVoucherService
                    .createContraVoucher(
                        payload
                    );


            navigate(
                `/finance/contra-vouchers/view/${response.id}`
            );

        }
        catch (error) {

            console.error(
                "Contra Voucher create failed:",
                error
            );


            setErrorMessage(
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                error?.message ||
                "Failed to create Contra Voucher."
            );

        }
        finally {

            setSaving(
                false
            );

        }

    }


    // ==================================================================
    // CANCEL
    // ==================================================================

    function handleCancel() {

        navigate(
            "/finance/contra-vouchers"
        );

    }


    // ==================================================================
    // FORMAT AMOUNT
    // ==================================================================

    function formatAmount(
        value
    ) {

        return Number(
            value || 0
        ).toLocaleString(
            "en-IN",
            {
                minimumFractionDigits:
                    2,

                maximumFractionDigits:
                    2,
            }
        );

    }


    // ==================================================================
    // RENDER
    // ==================================================================

    return (

        <>

            <FinancePageHeader

                icon="fas fa-exchange-alt"

                title="New Contra Voucher"

                subtitle={
                    "Create a Finance Contra Voucher."
                }

                breadcrumbs={[

                    {
                        label:
                            "Finance",

                        to:
                            "/finance",
                    },

                    {
                        label:
                            "Contra Voucher",

                        to:
                            "/finance/contra-vouchers",
                    },

                    {
                        label:
                            "New",
                    },

                ]}

                actions={

                    <div
                        className="
                            d-flex
                            gap-2
                        "
                    >

                        <button

                            type="button"

                            className="
                                btn
                                btn-outline-secondary
                            "

                            onClick={
                                handleCancel
                            }

                            disabled={
                                saving
                            }

                        >

                            <i
                                className="
                                    fas
                                    fa-arrow-left
                                    me-1
                                "
                            />

                            Back to List

                        </button>


                        <button

                            type="button"

                            className="
                                btn
                                btn-primary
                            "

                            onClick={
                                handleSaveDraft
                            }

                            disabled={
                                saving
                            }

                        >

                            <i
                                className="
                                    fas
                                    fa-save
                                    me-1
                                "
                            />

                            {
                                saving
                                    ? "Saving..."
                                    : "Save Draft"
                            }

                        </button>

                    </div>

                }

            />


            {
                errorMessage && (

                    <div
                        className="
                            alert
                            alert-danger
                            mt-3
                        "
                    >

                        {errorMessage}

                    </div>

                )
            }


            {/* ==========================================================
                VOUCHER DETAILS
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="card-body"
                >

                    <h5
                        className="
                            text-primary
                            border-bottom
                            pb-2
                            mb-4
                        "
                    >
                        Voucher Details
                    </h5>


                    <div
                        className="row g-3"
                    >

                        {/* COMPANY */}

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Company

                                <span
                                    className="text-danger"
                                >
                                    {" *"}
                                </span>

                            </label>


                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadCompanies
                                }

                                isClearable

                                isDisabled={
                                    saving
                                }

                                value={
                                    formData.company_option
                                }

                                onChange={
                                    option =>
                                        handleFieldChange(
                                            "company_id",
                                            option?.value ||
                                            null,
                                            option
                                        )
                                }

                                placeholder="Select Company..."

                                menuPortalTarget={
                                    document.body
                                }

                                styles={{
                                    menuPortal: base => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                }}

                            />

                        </div>


                        {/* VOUCHER DATE */}

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Voucher Date

                                <span
                                    className="text-danger"
                                >
                                    {" *"}
                                </span>

                            </label>


                            <input

                                type="date"

                                className="form-control"

                                value={
                                    formData.voucher_date
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "voucher_date",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                            />

                        </div>


                        {/* FROM ACCOUNT */}

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                From Account

                                <span
                                    className="text-danger"
                                >
                                    {" *"}
                                </span>

                            </label>


                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadAccounts
                                }

                                isClearable

                                isDisabled={
                                    !formData.company_id ||
                                    saving
                                }

                                value={
                                    formData.from_account_option
                                }

                                onChange={
                                    option =>
                                        handleFieldChange(
                                            "from_account_id",
                                            option?.value ||
                                            null,
                                            option
                                        )
                                }

                                placeholder={
                                    !formData.company_id
                                        ? "Select Company First..."
                                        : "Search From Account..."
                                }

                                menuPortalTarget={
                                    document.body
                                }

                                styles={{
                                    menuPortal: base => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                }}

                            />

                        </div>


                        {/* TO ACCOUNT */}

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                To Account

                                <span
                                    className="text-danger"
                                >
                                    {" *"}
                                </span>

                            </label>


                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadAccounts
                                }

                                isClearable

                                isDisabled={
                                    !formData.company_id ||
                                    saving
                                }

                                value={
                                    formData.to_account_option
                                }

                                onChange={
                                    option =>
                                        handleFieldChange(
                                            "to_account_id",
                                            option?.value ||
                                            null,
                                            option
                                        )
                                }

                                placeholder={
                                    !formData.company_id
                                        ? "Select Company First..."
                                        : "Search To Account..."
                                }

                                menuPortalTarget={
                                    document.body
                                }

                                styles={{
                                    menuPortal: base => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                }}

                            />

                        </div>


                        {/* CURRENCY */}

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Currency

                                <span
                                    className="text-danger"
                                >
                                    {" *"}
                                </span>

                            </label>


                            <AsyncSelect

                                cacheOptions

                                defaultOptions

                                loadOptions={
                                    loadCurrencies
                                }

                                isClearable

                                isDisabled={
                                    saving
                                }

                                value={
                                    formData.currency_option
                                }

                                onChange={
                                    option =>
                                        handleFieldChange(
                                            "currency_id",
                                            option?.value ||
                                            null,
                                            option
                                        )
                                }

                                placeholder="Select Currency..."

                                menuPortalTarget={
                                    document.body
                                }

                                styles={{
                                    menuPortal: base => ({
                                        ...base,
                                        zIndex: 9999,
                                    }),
                                }}

                            />

                        </div>


                        {/* EXCHANGE RATE */}

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >

                                Exchange Rate

                                <span
                                    className="text-danger"
                                >
                                    {" *"}
                                </span>

                            </label>


                            <input

                                type="number"

                                min="0.00000001"

                                step="0.00000001"

                                className="form-control"

                                value={
                                    formData.exchange_rate
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "exchange_rate",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                            />

                        </div>


                        {/* REFERENCE NO */}

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Reference No.
                            </label>


                            <input

                                type="text"

                                className="form-control"

                                value={
                                    formData.reference_no
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "reference_no",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                                placeholder="Reference No."

                            />

                        </div>


                        {/* REFERENCE DATE */}

                        <div
                            className="
                                col-lg-3
                                col-md-6
                            "
                        >

                            <label
                                className="form-label"
                            >
                                Reference Date
                            </label>


                            <input

                                type="date"

                                className="form-control"

                                value={
                                    formData.reference_date
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "reference_date",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                            />

                        </div>


                        {/* REMARKS */}

                        <div
                            className="col-12"
                        >

                            <label
                                className="form-label"
                            >
                                Remarks
                            </label>


                            <textarea

                                className="form-control"

                                rows="3"

                                value={
                                    formData.remarks
                                }

                                onChange={
                                    event =>
                                        handleFieldChange(
                                            "remarks",
                                            event.target.value
                                        )
                                }

                                disabled={
                                    saving
                                }

                                placeholder="Enter remarks..."

                            />

                        </div>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                ALLOCATION / DIMENSIONS
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="card-body"
                >

                    <div
                        className="
                            d-flex
                            justify-content-between
                            align-items-center
                            border-bottom
                            pb-2
                            mb-3
                        "
                    >

                        <h5
                            className="
                                text-primary
                                mb-0
                            "
                        >
                            Allocation / Dimensions
                        </h5>


                        <button

                            type="button"

                            className="
                                btn
                                btn-outline-primary
                                btn-sm
                            "

                            onClick={
                                addLine
                            }

                            disabled={
                                saving
                            }

                        >

                            <i
                                className="
                                    fas
                                    fa-plus
                                    me-1
                                "
                            />

                            Add Line

                        </button>

                    </div>


                    <div
                        className="table-responsive"
                    >

                        <table
                            className="
                                table
                                table-bordered
                                align-middle
                            "
                        >

                            <thead
                                className="table-light"
                            >

                                <tr>

                                    <th
                                        style={{
                                            width: 60,
                                        }}
                                    >
                                        #
                                    </th>

                                    <th
                                        style={{
                                            minWidth: 150,
                                        }}
                                    >
                                        Amount
                                    </th>

                                    <th
                                        style={{
                                            minWidth: 220,
                                        }}
                                    >
                                        Cost Centre
                                    </th>

                                    
                                    <th
                                        style={{
                                            minWidth: 260,
                                        }}
                                    >
                                        Description
                                    </th>

                                    <th
                                        style={{
                                            width: 70,
                                        }}
                                    >
                                    </th>

                                </tr>

                            </thead>


                            <tbody>

                                {
                                    lines.map(
                                        (
                                            line,
                                            index
                                        ) => (

                                            <tr
                                                key={
                                                    index
                                                }
                                            >

                                                {/* SEQUENCE */}

                                                <td>
                                                    {
                                                        index + 1
                                                    }
                                                </td>


                                                {/* AMOUNT */}

                                                <td>

                                                    <input

                                                        type="number"

                                                        min="0"

                                                        step="0.01"

                                                        className="form-control"

                                                        value={
                                                            line.transaction_amount
                                                        }

                                                        onChange={
                                                            event =>
                                                                handleLineChange(
                                                                    index,
                                                                    "transaction_amount",
                                                                    event.target.value
                                                                )
                                                        }

                                                        disabled={
                                                            saving
                                                        }

                                                        placeholder="0.00"

                                                    />

                                                </td>


                                                {/* COST CENTRE */}

                                                <td>

                                                    <AsyncSelect

                                                        cacheOptions

                                                        defaultOptions

                                                        loadOptions={
                                                            loadCostCenters
                                                        }

                                                        isClearable

                                                        isDisabled={
                                                            !formData.company_id ||
                                                            saving
                                                        }

                                                        value={
                                                            line.cost_center_option
                                                        }

                                                        onChange={
                                                            option =>
                                                                handleLineChange(
                                                                    index,
                                                                    "cost_center_id",
                                                                    option?.value ||
                                                                    null,
                                                                    option
                                                                )
                                                        }

                                                        placeholder="Select Cost Centre..."

                                                        menuPortalTarget={
                                                            document.body
                                                        }

                                                        styles={{
                                                            menuPortal: base => ({
                                                                ...base,
                                                                zIndex: 9999,
                                                            }),
                                                        }}

                                                    />

                                                </td>


                                                

                                                {/* DESCRIPTION */}

                                                <td>

                                                    <input

                                                        type="text"

                                                        className="form-control"

                                                        value={
                                                            line.description
                                                        }

                                                        onChange={
                                                            event =>
                                                                handleLineChange(
                                                                    index,
                                                                    "description",
                                                                    event.target.value
                                                                )
                                                        }

                                                        disabled={
                                                            saving
                                                        }

                                                        placeholder="Description"

                                                    />

                                                </td>


                                                {/* DELETE */}

                                                <td
                                                    className="text-center"
                                                >

                                                    <button

                                                        type="button"

                                                        className="
                                                            btn
                                                            btn-outline-danger
                                                            btn-sm
                                                        "

                                                        onClick={() =>
                                                            removeLine(
                                                                index
                                                            )
                                                        }

                                                        disabled={
                                                            saving ||
                                                            lines.length <= 1
                                                        }

                                                    >

                                                        <i
                                                            className="
                                                                fas
                                                                fa-trash
                                                            "
                                                        />

                                                    </button>

                                                </td>

                                            </tr>

                                        )
                                    )
                                }

                            </tbody>

                        </table>

                    </div>


                    {/* ==================================================
                        TOTALS
                    ================================================== */}

                    <div
                        className="
                            d-flex
                            justify-content-end
                            mt-3
                        "
                    >

                        <div
                            className="
                                border
                                rounded
                                p-3
                                bg-light
                            "
                            style={{
                                minWidth:
                                    350,
                            }}
                        >

                            <div
                                className="
                                    d-flex
                                    justify-content-between
                                "
                            >

                                <strong>
                                    Total Contra Amount
                                </strong>

                                <strong
                                    className="
                                        text-primary
                                    "
                                >
                                    {
                                        formatAmount(
                                            totalAmount
                                        )
                                    }
                                </strong>

                            </div>


                            <div
                                className="
                                    d-flex
                                    justify-content-between
                                    text-muted
                                    mt-1
                                "
                            >

                                <span>
                                    Base Amount
                                </span>

                                <span>
                                    {
                                        formatAmount(
                                            totalBaseAmount
                                        )
                                    }
                                </span>

                            </div>

                        </div>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                ACCOUNTING PREVIEW
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                "
            >

                <div
                    className="card-body"
                >

                    <h5
                        className="
                            text-primary
                            border-bottom
                            pb-2
                            mb-3
                        "
                    >
                        Accounting Preview
                    </h5>


                    <div
                        className="
                            row
                            g-3
                        "
                    >

                        <div
                            className="col-md-6"
                        >

                            <div
                                className="
                                    border
                                    rounded
                                    p-3
                                    bg-light
                                "
                            >

                                <div
                                    className="text-muted"
                                >
                                    Debit
                                </div>

                                <strong>
                                    {
                                        formData.to_account_option?.label ||
                                        "To Account"
                                    }
                                </strong>

                                <div
                                    className="
                                        text-end
                                        fw-bold
                                        text-success
                                    "
                                >
                                    {
                                        formatAmount(
                                            totalBaseAmount
                                        )
                                    }
                                </div>

                            </div>

                        </div>


                        <div
                            className="col-md-6"
                        >

                            <div
                                className="
                                    border
                                    rounded
                                    p-3
                                    bg-light
                                "
                            >

                                <div
                                    className="text-muted"
                                >
                                    Credit
                                </div>

                                <strong>
                                    {
                                        formData.from_account_option?.label ||
                                        "From Account"
                                    }
                                </strong>

                                <div
                                    className="
                                        text-end
                                        fw-bold
                                        text-danger
                                    "
                                >
                                    {
                                        formatAmount(
                                            totalBaseAmount
                                        )
                                    }
                                </div>

                            </div>

                        </div>

                    </div>

                </div>

            </div>


            {/* ==========================================================
                FOOTER
            ========================================================== */}

            <div
                className="
                    card
                    shadow-sm
                    border-0
                    mt-3
                    mb-4
                "
            >

                <div
                    className="
                        card-body
                        d-flex
                        justify-content-between
                        align-items-center
                    "
                >

                    <div>

                        <span
                            className="text-danger"
                        >
                            *
                        </span>

                        {" "}
                        Mandatory Fields

                    </div>


                    <div
                        className="
                            d-flex
                            gap-2
                        "
                    >

                        <button

                            type="button"

                            className="
                                btn
                                btn-outline-secondary
                            "

                            onClick={
                                handleCancel
                            }

                            disabled={
                                saving
                            }

                        >
                            Cancel
                        </button>


                        <button

                            type="button"

                            className="
                                btn
                                btn-primary
                            "

                            onClick={
                                handleSaveDraft
                            }

                            disabled={
                                saving
                            }

                        >

                            <i
                                className="
                                    fas
                                    fa-save
                                    me-1
                                "
                            />

                            {
                                saving
                                    ? "Saving..."
                                    : "Save Draft"
                            }

                        </button>

                    </div>

                </div>

            </div>

        </>

    );

}